// This file is part of ecbyts/ECBT (Environmental & Occupational Core Byte Tools).
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).
// See LICENSE file or https://www.gnu.org/licenses/agpl-3.0.txt

/**
 * CBMD-001 / CR-031 — mapa pure-logic de erros técnicos de pagamento → mensagem
 * amigável (claude - billing, 2026-07-15).
 *
 * EN: Pure mapping from raw provider/edge-function error codes (and internal
 *     thrown messages) to a friendly, actionable customer-facing string. Zero
 *     DOM/i18n/network deps — returns the i18n *key* to try plus a hard PT-BR
 *     fallback so the consumer keeps the existing `t(key) || fallback` pattern.
 *     Never leaks internal codes ('invalid_currency', 'rate_limit_exceeded',
 *     stack traces, Mercado Pago raw payloads) to the end user.
 *
 * PT (leigo): esta é a "tradução" da mensagem crua de erro que o servidor de
 *     pagamento devolve (em inglês/código interno) para um texto que o cliente
 *     entende e sabe o que fazer ("Cartão recusado, tente outro"). Não fala com
 *     tela nem rede — só transforma texto em texto.
 *
 * Refs:
 *   - docs/reports/COMMERCIAL_READINESS_BASELINE_2026_07_02.json CR-031
 *   - supabase/functions/ecbt-topup/index.ts + ecbt-checkout/index.ts (códigos)
 *   - CDC Art 6 III / Art 31 (informação clara e adequada)
 */

export interface FriendlyPaymentError {
    /** Chave i18n a tentar (consumidor faz `t(key) || fallback`). */
    key: string;
    /** Fallback PT-BR embutido — nunca vaza código técnico. */
    fallback: string;
    /**
     * T6.3b — true quando a mensagem é o fallback genérico (erro NÃO mapeado
     * ou interno sem ação clara). O consumidor deve anexar um código de
     * referência (`buildPaymentErrorRef`) para o usuário citar ao suporte.
     */
    generic?: boolean;
}

/**
 * Fallback genérico e seguro — usado quando nenhum código específico casa.
 * Nunca concatena a mensagem crua.
 */
const GENERIC: FriendlyPaymentError = Object.freeze({
    key: 'checkout.error.generic',
    fallback: 'Não foi possível concluir o pagamento. Tente novamente em instantes.',
    generic: true,
});

const RATE_EXPIRED: FriendlyPaymentError = Object.freeze({
    key: 'checkout.error.rateExpired',
    fallback: 'A cotação expirou. Reabra o checkout para atualizar o preço.',
});

const UNAVAILABLE: FriendlyPaymentError = Object.freeze({
    key: 'checkout.error.unavailable',
    fallback: 'Este item não está disponível para compra agora. Nada foi cobrado.',
    generic: true,
});

/**
 * Códigos internos/edge-function → mensagem amigável. As chaves são
 * normalizadas (lowercase, trim) antes do lookup.
 */
const CODE_MAP: Readonly<Record<string, FriendlyPaymentError>> = Object.freeze({
    rate_limit_exceeded: {
        key: 'checkout.error.rateLimit',
        fallback: 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente de novo.',
    },
    invalid_ecbt_amount: {
        key: 'checkout.error.invalidAmount',
        fallback: 'Valor de compra inválido. Revise a quantidade e tente novamente.',
    },
    invalid_fiat_cents: {
        key: 'checkout.error.invalidAmount',
        fallback: 'Valor de compra inválido. Revise a quantidade e tente novamente.',
    },
    invalid_currency: {
        key: 'checkout.error.invalidCurrency',
        fallback: 'Moeda não suportada para esta compra. Escolha outra moeda.',
    },
    rate_snapshot_too_large: {
        key: 'checkout.error.rateExpired',
        fallback: 'A cotação expirou. Reabra o checkout para atualizar o preço.',
    },
    idempotency_key_conflict: {
        key: 'checkout.error.duplicate',
        fallback: 'Já existe um pagamento em andamento para este item. Aguarde a confirmação.',
    },
    internal_error: {
        key: 'checkout.error.generic',
        fallback: GENERIC.fallback,
        generic: true,
    },
    mp_error: {
        key: 'checkout.error.provider',
        fallback: 'O provedor de pagamento recusou a operação. Tente outro cartão ou método.',
    },
    // Mercado Pago desligado para assinatura (SUBSCRIPTION_MP_CHECKOUT_ENABLED).
    provider_disabled: {
        key: 'checkout.error.providerDisabled',
        fallback: 'Este meio de pagamento não está disponível. Pague com cartão pelo Stripe.',
    },
    // CHECKOUT-HARDENING-001 H32 — códigos novos das edges endurecidas (B1).
    // Preço/cotação mudou ou pacote não reproduz o catálogo: reabrir resolve.
    rate_mismatch: RATE_EXPIRED,
    price_changed: RATE_EXPIRED,
    unknown_package: RATE_EXPIRED,
    legacy_amount_mismatch: RATE_EXPIRED,
    unsupported_currency: {
        key: 'checkout.error.invalidCurrency',
        fallback: 'Moeda não suportada para esta compra. Escolha outra moeda.',
    },
    // Item sem preço no servidor / sem escritor de direito / nível sem preço /
    // compra desligada por flag: nada foi cobrado e não adianta tentar de novo
    // agora — mensagem clara + código de referência para o suporte.
    price_unresolved: UNAVAILABLE,
    item_not_found: UNAVAILABLE,
    recharge_catalog_invalid: UNAVAILABLE,
    entitlement_not_supported: UNAVAILABLE,
    tier_price_unavailable: UNAVAILABLE,
    tier_selection_mismatch: UNAVAILABLE,
    invalid_tier: UNAVAILABLE,
    price_selection_not_found: UNAVAILABLE,
    price_catalog_unavailable: UNAVAILABLE,
    legacy_price_unavailable: UNAVAILABLE,
    market_v2_purchase_disabled: UNAVAILABLE,
    seller_payout_unavailable: UNAVAILABLE,
    topup_unavailable: UNAVAILABLE,
    price_below_minimum: UNAVAILABLE,
    // library-checkout 403: oferta restrita a uma organização e o comprador não é
    // membro (libraries.visible_org_id). Nada foi cobrado; código de referência.
    course_not_available: UNAVAILABLE,
    // "Já é dono" (library-checkout 400 already_owned): nada foi cobrado.
    already_owned: {
        key: 'checkout.alreadyOwned',
        fallback: 'Você já tem esta biblioteca — nada foi cobrado.',
    },
    // Pro: já tem plano vivo (mensal ao pedir o anual, ou anual no prazo ao
    // pedir o mensal). Nada foi cobrado.
    active_subscription_exists: {
        key: 'checkout.error.activeSubscription',
        fallback: 'Você já tem um plano Pro ativo — nada foi cobrado.',
    },
    invalid_billing_cycle: UNAVAILABLE,
    // H57: CPF/CNPJ do comprador (nota fiscal) informado com erro de digitação.
    buyer_document_invalid: {
        key: 'checkout.error.buyerDocument',
        fallback: 'CPF/CNPJ inválido. Confira os números e tente de novo.',
    },
    // H57 obrigatório (LIBRARY_BUYER_FISCAL_REQUIRED): venda própria sem CPF/CNPJ.
    // Reusa a chave da frente AESAS, já traduzida nos 16 idiomas.
    buyer_document_required: {
        key: 'lms.buyCourse.errorDocumentRequired',
        fallback: 'Informe o CPF ou CNPJ de quem compra.',
    },
});

/**
 * Substrings de mensagens internas cruas (lançadas no client) → amigável.
 * Casadas por inclusão, case-insensitive, após o lookup exato de código falhar.
 */
const SUBSTRING_MAP: ReadonlyArray<readonly [string, FriendlyPaymentError]> = Object.freeze([
    [
        'supabase client',
        {
            key: 'checkout.error.offline',
            fallback: 'Não foi possível conectar ao pagamento. Verifique sua conexão e tente novamente.',
        },
    ],
    [
        'url',
        {
            key: 'checkout.error.generic',
            fallback: GENERIC.fallback,
            generic: true,
        },
    ],
    [
        'network',
        {
            key: 'checkout.error.offline',
            fallback: 'Falha de conexão. Verifique sua internet e tente novamente.',
        },
    ],
    [
        'failed to fetch',
        {
            key: 'checkout.error.offline',
            fallback: 'Falha de conexão. Verifique sua internet e tente novamente.',
        },
    ],
    [
        'timeout',
        {
            key: 'checkout.error.offline',
            fallback: 'A operação demorou demais. Tente novamente em instantes.',
        },
    ],
    [
        'card',
        {
            key: 'checkout.error.card',
            fallback: 'Cartão recusado. Verifique os dados ou tente outro cartão.',
        },
    ],
    [
        'declined',
        {
            key: 'checkout.error.card',
            fallback: 'Cartão recusado. Verifique os dados ou tente outro cartão.',
        },
    ],
]);

/**
 * Mapeia um erro cru de pagamento (código, mensagem interna ou objeto de erro)
 * para uma mensagem amigável e traduzível. NUNCA devolve o texto técnico cru.
 *
 * @param raw - `err.message`, `data.error`, código, ou o próprio Error.
 * @returns `{ key, fallback }` — consumidor usa `t(key) || fallback`.
 */
export function mapPaymentError(raw: unknown): FriendlyPaymentError {
    const msg = _extractMessage(raw);
    if (!msg) return GENERIC;

    const normalized = msg.trim().toLowerCase();

    // 1) Lookup exato por código (ex.: 'invalid_currency', 'rate_limit_exceeded').
    if (Object.prototype.hasOwnProperty.call(CODE_MAP, normalized)) {
        return CODE_MAP[normalized] as FriendlyPaymentError;
    }

    // 2) Match por substring (mensagens internas cruas / provider).
    for (const [needle, friendly] of SUBSTRING_MAP) {
        if (normalized.includes(needle)) return friendly;
    }

    // 3) Nenhum casou → genérico seguro (nunca vaza o cru).
    return GENERIC;
}

/**
 * T6.3b — código de referência curto para o usuário citar ao suporte quando o
 * erro cai no fallback genérico. Formato: `ERR-XXXXXX` (6 chars base36).
 *
 * Pure: derivado só de `nowMs` (injetado) + hash do erro cru — sem relógio
 * interno, sem aleatoriedade. O mesmo instante + mesmo erro → mesmo código,
 * o que permite ao suporte correlacionar com o log (`log.error` grava o cru
 * junto do mesmo código).
 *
 * @param nowMs Epoch ms do momento do erro (injetado para pure).
 * @param raw   Erro cru (mensagem/código/Error) — entra só no hash.
 */
export function buildPaymentErrorRef(nowMs: number, raw?: unknown): string {
    const n = Number(nowMs);
    const time = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    // 36^4 janelas de ~1ms → 4 chars que variam no tempo.
    const timePart = (time % 1679616).toString(36).toUpperCase().padStart(4, '0');
    const msg = _extractMessage(raw);
    let h = 0;
    for (let i = 0; i < msg.length; i++) h = (h * 31 + msg.charCodeAt(i)) >>> 0;
    const hashPart = (h % 1296).toString(36).toUpperCase().padStart(2, '0');
    return `ERR-${timePart}${hashPart}`;
}

function _extractMessage(raw: unknown): string {
    if (raw == null) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') {
        const anyRaw = raw as Record<string, unknown>;
        const candidate = anyRaw.message ?? anyRaw.error ?? anyRaw.code;
        if (typeof candidate === 'string') return candidate;
    }
    return '';
}
